import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function SettingsPage() {
  const [s, setS] = React.useState(null);
  const [counters, setCounters] = React.useState({});
  const [counterEdit, setCounterEdit] = React.useState({}); // {INV: "100", ...}

  const load = React.useCallback(async () => {
    const [set, cnt] = await Promise.all([api.get("/settings"), api.get("/settings/counters")]);
    setS(set.data); setCounters(cnt.data);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.patch("/settings", s); toast.success("Pengaturan disimpan"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const DANGER_PIN = "28292829";
  const requirePin = (label) => {
    const pin = window.prompt(`Masukkan PIN Zona Berbahaya untuk melanjutkan "${label}":`);
    if (pin === null) return false;
    if (pin !== DANGER_PIN) { toast.error("PIN salah. Aksi dibatalkan."); return false; }
    return true;
  };

  const wipe = async () => {
    if (!requirePin("Hapus Semua Data Demo")) return;
    try { await api.post("/admin/wipe-demo"); toast.success("Data demo dihapus. Muat ulang halaman."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const seed = async () => {
    if (!requirePin("Muat Ulang Data Demo")) return;
    try { await api.post("/admin/seed?force=true"); toast.success("Data demo dimuat ulang. Muat ulang halaman."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setCounter = async (prefix) => {
    const value = parseInt(counterEdit[prefix]);
    if (isNaN(value) || value < 0) return toast.error("Nilai counter tidak valid");
    try {
      await api.post("/settings/counters", { prefix, value });
      toast.success(`Counter ${prefix} di-set. Nomor berikutnya = ${value + 1}`);
      setCounterEdit({ ...counterEdit, [prefix]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const previewNumber = (code) => {
    if (!s) return "";
    const prefix = (s.doc_prefix || "GRP").toUpperCase();
    const ym = new Date().toISOString().slice(0, 7).replace("-", "");
    const codeMap = { QTN: s.qtn_code, INV: s.inv_code, BKG: s.bkg_code, RCP: s.rcp_code, MNT: s.mnt_code };
    const c = (codeMap[code] || code).toUpperCase();
    const next = String((counters[code]?.next || 1)).padStart(4, "0");
    return `${prefix}-${c}-${ym}-${next}`;
  };

  if (!s) return <div className="text-sm text-neutral-500">Memuat…</div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Pengaturan</h1>
        <p className="text-sm text-neutral-500">Konfigurasi bisnis, branding, dan data demo.</p>
      </div>

      <div className="card-flat p-4 space-y-3">
        <div className="text-sm font-semibold">Informasi Bisnis</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nama Bisnis</Label><Input value={s.business_name || ""} onChange={(e) => setS({ ...s, business_name: e.target.value })} data-testid="s-name" /></div>
          <div><Label>Website</Label><Input value={s.business_website || ""} onChange={(e) => setS({ ...s, business_website: e.target.value })} /></div>
          <div><Label>Telepon</Label><Input value={s.business_phone || ""} onChange={(e) => setS({ ...s, business_phone: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={s.business_email || ""} onChange={(e) => setS({ ...s, business_email: e.target.value })} /></div>
          <div className="col-span-2"><Label>Alamat</Label><Textarea rows={2} value={s.business_address || ""} onChange={(e) => setS({ ...s, business_address: e.target.value })} /></div>
        </div>
      </div>

      <div className="card-flat p-4 space-y-3" data-testid="sop-card">
        <div className="text-sm font-semibold">Dokumen SOP</div>
        <p className="text-xs text-neutral-500">Unduh dokumen Standar Operasional Prosedur (1 halaman, landscape) berisi alur bisnis lengkap dan matrix kewenangan untuk semua role. Bagikan ke tim untuk onboarding.</p>
        <Button
          onClick={async () => {
            try {
              const res = await api.get("/sop/pdf", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              window.open(url, "_blank");
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="bg-green-600 hover:bg-green-700"
          data-testid="download-sop-btn"
        >
          Unduh SOP (PDF)
        </Button>
      </div>

      <div className="card-flat p-4 space-y-3">
        <div className="text-sm font-semibold">Branding</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Warna Utama</Label>
            <div className="flex gap-2 items-center">
              <input type="color" value={s.brand_color || "#16a34a"} onChange={(e) => setS({ ...s, brand_color: e.target.value })} className="h-9 w-12 border border-neutral-200 rounded" data-testid="s-color" />
              <Input value={s.brand_color || ""} onChange={(e) => setS({ ...s, brand_color: e.target.value })} />
            </div>
          </div>
          <div><Label>Logo URL / Upload</Label>
            <div className="flex gap-2">
              <Input value={s.logo_url || ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })} placeholder="https://…  atau upload file di sebelah" data-testid="s-logo-url" />
              <input
                id="logo-file-upload"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 500 * 1024) { toast.error("Ukuran logo maks 500KB — kompres dulu ya"); e.target.value = ""; return; }
                  const reader = new FileReader();
                  reader.onload = () => {
                    setS({ ...s, logo_url: reader.result });
                    toast.success("Logo terupload — klik 'Simpan Pengaturan' untuk menyimpan");
                  };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }}
                data-testid="logo-file-input"
              />
              <Button variant="outline" onClick={() => document.getElementById("logo-file-upload").click()} data-testid="upload-logo-btn">Upload</Button>
            </div>
            {s.logo_url && (
              <div className="mt-2 flex items-center gap-3 p-2 border border-neutral-200 rounded bg-white">
                <img src={s.logo_url} alt="logo preview" className="h-16 w-16 object-contain rounded" data-testid="logo-preview" onError={(ev) => { ev.currentTarget.style.opacity = "0.3"; }} />
                <div>
                  <div className="text-xs text-neutral-600">Preview logo yang akan muncul di PDF Quotation & Invoice</div>
                  <button className="text-xs text-red-600 hover:underline mt-1" onClick={() => setS({ ...s, logo_url: "" })} data-testid="clear-logo">Hapus logo</button>
                </div>
              </div>
            )}
            <div className="text-[10px] text-neutral-500 mt-0.5">Upload file PNG/JPG (maks 500KB) atau isi URL publik. Setelah simpan, muat ulang PDF quotation/invoice untuk melihat.</div>
          </div>
        </div>
      </div>

      <div className="card-flat p-4 space-y-3">
        <div className="text-sm font-semibold">Keuangan & Bank</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>PPN Default (%)</Label><Input type="number" value={s.tax_rate || 0} onChange={(e) => setS({ ...s, tax_rate: +e.target.value })} data-testid="s-tax" /></div>
          <div><Label>Bank</Label><Input value={s.bank_name || ""} onChange={(e) => setS({ ...s, bank_name: e.target.value })} /></div>
          <div><Label>Nama Rekening</Label><Input value={s.bank_account_name || ""} onChange={(e) => setS({ ...s, bank_account_name: e.target.value })} /></div>
          <div><Label>No. Rekening</Label><Input value={s.bank_account_number || ""} onChange={(e) => setS({ ...s, bank_account_number: e.target.value })} /></div>
          <div className="col-span-2"><Label>Syarat & Ketentuan Default</Label><Textarea rows={3} value={s.default_terms || ""} onChange={(e) => setS({ ...s, default_terms: e.target.value })} /></div>
        </div>
      </div>

      <div className="card-flat p-4 space-y-3">
        <div className="text-sm font-semibold">Ketentuan Dokumen</div>
        <div className="text-xs text-neutral-500 -mt-1">Teks berikut akan muncul di PDF Quotation dan Invoice. Kosongkan jika tidak diperlukan.</div>
        <div><Label>Syarat & Ketentuan Quotation</Label>
          <Textarea rows={4} value={s.quotation_terms || ""} onChange={(e) => setS({ ...s, quotation_terms: e.target.value })} placeholder="Berlaku 7 hari. DP 50% sebelum keluar gudang. Pelunasan sebelum equipment kembali." data-testid="s-qtn-terms" />
        </div>
        <div><Label>Syarat & Ketentuan Invoice</Label>
          <Textarea rows={4} value={s.invoice_terms || ""} onChange={(e) => setS({ ...s, invoice_terms: e.target.value })} placeholder="Kerusakan/kehilangan menjadi tanggung jawab penyewa sesuai replacement value. Denda keterlambatan Rp 100.000/hari." data-testid="s-inv-terms" />
        </div>
        <div><Label>Ketentuan Pembayaran</Label>
          <Textarea rows={3} value={s.payment_terms || ""} onChange={(e) => setS({ ...s, payment_terms: e.target.value })} placeholder="Transfer via BCA a.n. PT Grippel Rental Indonesia. Kirim bukti transfer via WhatsApp." data-testid="s-payment-terms" />
          <div className="text-[10px] text-neutral-500 mt-0.5">Muncul di PDF Quotation dan Invoice, di atas syarat & ketentuan.</div>
        </div>
      </div>

      <div className="card-flat p-4 space-y-3">
        <div className="text-sm font-semibold">Nomor Dokumen</div>
        <div className="text-xs text-neutral-500 -mt-1">Format: <span className="font-mono">{"{PREFIX}-{KODE}-{YYYYMM}-{XXXX}"}</span></div>
        <div>
          <Label>Prefix Perusahaan</Label>
          <Input value={s.doc_prefix || ""} onChange={(e) => setS({ ...s, doc_prefix: e.target.value.toUpperCase() })} placeholder="GRP" maxLength={6} data-testid="s-doc-prefix" />
          <div className="text-[10px] text-neutral-500 mt-0.5">Prefix umum yang muncul di awal semua nomor dokumen (contoh: GRP, GPR, GR).</div>
        </div>
        <div>
          <Label>Prefix Kode Aset</Label>
          <Input value={s.asset_prefix || ""} onChange={(e) => setS({ ...s, asset_prefix: e.target.value.toUpperCase() })} placeholder="GRP" maxLength={6} data-testid="s-asset-prefix" />
          <div className="text-[10px] text-neutral-500 mt-0.5">Prefix untuk kode aset fisik saat auto-generate (contoh: GRP-0001, GRP-0002).</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><Label>Kode Quotation</Label><Input value={s.qtn_code || ""} onChange={(e) => setS({ ...s, qtn_code: e.target.value.toUpperCase() })} placeholder="QTN" maxLength={5} data-testid="s-qtn-code" /></div>
          <div><Label>Kode Invoice</Label><Input value={s.inv_code || ""} onChange={(e) => setS({ ...s, inv_code: e.target.value.toUpperCase() })} placeholder="INV" maxLength={5} data-testid="s-inv-code" /></div>
          <div><Label>Kode Booking</Label><Input value={s.bkg_code || ""} onChange={(e) => setS({ ...s, bkg_code: e.target.value.toUpperCase() })} placeholder="BKG" maxLength={5} data-testid="s-bkg-code" /></div>
          <div><Label>Kode Kuitansi</Label><Input value={s.rcp_code || ""} onChange={(e) => setS({ ...s, rcp_code: e.target.value.toUpperCase() })} placeholder="RCP" maxLength={5} data-testid="s-rcp-code" /></div>
          <div><Label>Kode Maintenance</Label><Input value={s.mnt_code || ""} onChange={(e) => setS({ ...s, mnt_code: e.target.value.toUpperCase() })} placeholder="MNT" maxLength={5} data-testid="s-mnt-code" /></div>
        </div>

        <div className="border-t border-neutral-200 pt-3">
          <div className="text-sm font-medium mb-2">Nomor Terakhir Bulan Ini</div>
          <div className="text-xs text-neutral-500 mb-3">Ubah nilai counter di bawah untuk melompat ke nomor tertentu (mis. mulai dari 100). Nomor berikutnya akan menjadi <b>counter + 1</b>. Perubahan berlaku untuk bulan berjalan saja; bulan depan counter reset otomatis ke 1.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {["QTN", "INV", "BKG", "RCP", "MNT"].map((k) => (
              <div key={k} className="flex items-end gap-2 p-2 rounded border border-neutral-200">
                <div className="flex-1">
                  <div className="text-xs font-semibold text-neutral-700">{k}</div>
                  <div className="text-[11px] text-neutral-500 font-mono">Preview berikutnya: <span className="text-green-700">{previewNumber(k)}</span></div>
                  <div className="text-[11px] text-neutral-500">Terakhir dipakai: <b>{counters[k]?.current || 0}</b></div>
                </div>
                <Input
                  type="number" min={0} placeholder="Set counter…"
                  className="w-32"
                  value={counterEdit[k] || ""}
                  onChange={(e) => setCounterEdit({ ...counterEdit, [k]: e.target.value })}
                  data-testid={`counter-input-${k}`}
                />
                <Button size="sm" variant="outline" onClick={() => setCounter(k)} data-testid={`counter-set-${k}`}>Set</Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end sticky bottom-0 bg-white py-3 border-t border-neutral-200 -mx-6 px-6">
        <Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-settings">Simpan Pengaturan</Button>
      </div>

      <div className="card-flat p-4 border-red-200 bg-red-50">
        <div className="text-sm font-semibold text-red-900 mb-1">Zona Berbahaya</div>
        <div className="text-xs text-red-800 mb-3">Aksi berikut menghapus data secara <b>permanen</b>. Tidak dapat dibatalkan! Dilindungi dengan <b>PIN keamanan</b>.</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-100" data-testid="btn-wipe-inventory"
            onClick={async () => {
              if (!requirePin("Hapus Semua Inventory & Aset Fisik")) return;
              if (!window.confirm("KONFIRMASI FINAL: Semua produk, aset fisik, dan record maintenance akan DIHAPUS PERMANEN. Lanjutkan?")) return;
              try {
                const { data } = await api.post("/admin/wipe-inventory");
                toast.success(`Dihapus: ${data.products_deleted} produk, ${data.assets_deleted} aset, ${data.maintenance_deleted} maintenance`);
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
            Hapus Semua Inventory & Aset Fisik
          </Button>
          <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-100" data-testid="btn-wipe-transactions"
            onClick={async () => {
              if (!requirePin("Hapus Semua Transaksi")) return;
              if (!window.confirm("KONFIRMASI FINAL: Semua booking, quotation, invoice, pembayaran, notifikasi, audit log akan DIHAPUS PERMANEN. Pelanggan dan inventory tetap ada. Lanjutkan?")) return;
              try {
                const { data } = await api.post("/admin/wipe-all-transactions");
                toast.success(`Dihapus: ${data.bookings_deleted} booking, ${data.quotations_deleted} quotation, ${data.invoices_deleted} invoice, ${data.payments_deleted} pembayaran`);
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
            Hapus Semua Transaksi
          </Button>
        </div>
      </div>

      <div className="card-flat p-4 border-amber-200 bg-amber-50">
        <div className="text-sm font-semibold text-amber-900 mb-1">Data Demonstrasi</div>
        <div className="text-xs text-amber-800 mb-3">Data demo memiliki tanda <b>is_demo</b>. Hapus sebelum production.</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seed} data-testid="btn-seed">Muat Ulang Data Demo</Button>
          <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={wipe} data-testid="btn-wipe">Hapus Semua Data Demo</Button>
        </div>
      </div>
    </div>
  );
}
